/**
 * ============================================================
 * WHAM TAN — Shopify Theme JS
 * theme.js
 *
 * Sections:
 *   1. Mobile nav toggle
 *   2. Scroll reveal animation
 *   3. Smooth scroll for anchor links
 *   4. Sticky nav shadow on scroll
 *   5. AJAX Add to Cart (product cards)
 *   6. Product card: variant select → update price & variant ID
 *   7. Product card: subscription / selling plan toggle
 *   8. Cart count update helpers
 *   9. Newsletter form feedback
 * ============================================================
 */

(function () {
  'use strict';

  // ============================================================
  // 1. MOBILE NAV TOGGLE
  // ============================================================
  var menuToggle = document.getElementById('menuToggle');
  var mobileMenu = document.getElementById('mobileMenu');

  if (menuToggle && mobileMenu) {
    menuToggle.addEventListener('click', function () {
      var isOpen = mobileMenu.classList.toggle('open');
      menuToggle.setAttribute('aria-expanded', String(isOpen));
      mobileMenu.setAttribute('aria-hidden', String(!isOpen));

      var spans = menuToggle.querySelectorAll('span');
      if (isOpen) {
        spans[0].style.transform = 'rotate(45deg) translate(5px, 5px)';
        spans[1].style.opacity = '0';
        spans[2].style.transform = 'rotate(-45deg) translate(5px, -5px)';
      } else {
        spans[0].style.transform = '';
        spans[1].style.opacity = '';
        spans[2].style.transform = '';
      }
    });

    // Close mobile nav when a link is clicked
    mobileMenu.querySelectorAll('a').forEach(function (link) {
      link.addEventListener('click', function () {
        mobileMenu.classList.remove('open');
        menuToggle.setAttribute('aria-expanded', 'false');
        mobileMenu.setAttribute('aria-hidden', 'true');
        var spans = menuToggle.querySelectorAll('span');
        spans[0].style.transform = '';
        spans[1].style.opacity = '';
        spans[2].style.transform = '';
      });
    });
  }

  // ============================================================
  // 2. SCROLL REVEAL ANIMATION
  // ============================================================
  if ('IntersectionObserver' in window) {
    var revealObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            revealObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    document.querySelectorAll('.reveal').forEach(function (el) {
      revealObserver.observe(el);
    });
  } else {
    // Fallback: just show everything
    document.querySelectorAll('.reveal').forEach(function (el) {
      el.classList.add('visible');
    });
  }

  // ============================================================
  // 3. SMOOTH SCROLL FOR ANCHOR LINKS
  // ============================================================
  document.querySelectorAll('a[href^="#"]').forEach(function (anchor) {
    anchor.addEventListener('click', function (e) {
      var targetSelector = anchor.getAttribute('href');
      if (targetSelector === '#') return;
      var target = document.querySelector(targetSelector);
      if (target) {
        e.preventDefault();
        var nav = document.querySelector('.nav');
        var navHeight = nav ? nav.offsetHeight : 0;
        var targetY = target.getBoundingClientRect().top + window.scrollY - navHeight - 16;
        window.scrollTo({ top: targetY, behavior: 'smooth' });
      }
    });
  });

  // ============================================================
  // 4. STICKY NAV SHADOW ON SCROLL
  // ============================================================
  var siteNav = document.getElementById('site-nav') || document.querySelector('.nav');
  if (siteNav) {
    window.addEventListener(
      'scroll',
      function () {
        siteNav.style.boxShadow =
          window.scrollY > 60 ? '0 4px 24px rgba(0,0,0,0.6)' : '';
      },
      { passive: true }
    );
  }

  // ============================================================
  // 5. AJAX ADD TO CART — Product Cards
  //    Handles .product-card__atc buttons on collection
  //    and homepage featured-products sections.
  // ============================================================
  document.querySelectorAll('.product-card__atc').forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      // If it's a link, let it navigate naturally to product page
      if (btn.tagName === 'A') return;
      e.preventDefault();

      var productId = btn.dataset.productId;
      var variantId = parseInt(btn.dataset.variantId, 10);
      var card = btn.closest('[data-product-id]');

      // Check for variant select override
      if (card) {
        var variantSelect = card.querySelector('.product-card__variant-select');
        if (variantSelect) {
          variantId = parseInt(variantSelect.value, 10);
        }
      }

      if (!variantId) {
        console.warn('[WhamTan] No variant ID found for product', productId);
        return;
      }

      var body = { id: variantId, quantity: 1 };

      // Check for selling plan (new button-based toggle)
      if (card) {
        var activeTypeBtn = card.querySelector('.purchase-btn--active[data-type="subscription"]');
        var planInput = card.querySelector('[data-product-card-plan-select]');
        if (activeTypeBtn && planInput) {
          var spId = planInput.value || planInput.dataset.firstPlanId;
          if (spId) {
            body.selling_plan = parseInt(spId, 10);
            console.log('[WhamTan] Adding subscription with selling_plan:', body.selling_plan);
          }
        }
      }

      // Optimistic UI
      btn.disabled = true;
      var originalText = btn.textContent;
      btn.textContent = 'Adding…';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
      })
        .then(function (res) {
          return res.json();
        })
        .then(function (data) {
          if (data.status && data.status !== 200) {
            // Error from Shopify
            btn.textContent = data.description || 'Error';
            btn.style.background = 'var(--red)';
            setTimeout(function () {
              btn.textContent = originalText;
              btn.style.background = '';
              btn.disabled = false;
            }, 2200);
          } else {
            // Go straight to checkout
            window.location.href = '/checkout';
          }
        })
        .catch(function (err) {
          console.error('[WhamTan] Cart add error:', err);
          btn.textContent = 'Error — Try Again';
          btn.style.background = 'var(--red)';
          setTimeout(function () {
            btn.textContent = originalText;
            btn.style.background = '';
            btn.disabled = false;
          }, 2200);
        });
    });
  });

  // ============================================================
  // 6. PRODUCT CARD — Variant select → update price & variant ID
  // ============================================================
  document.querySelectorAll('.product-card__variant-select').forEach(function (select) {
    select.addEventListener('change', function () {
      var productId = select.dataset.productCard;
      var card = document.querySelector('[data-product-id="' + productId + '"]');
      if (!card) return;

      var selectedOption = select.options[select.selectedIndex];
      var price = selectedOption.dataset.price;
      var comparePrice = selectedOption.dataset.comparePrice;

      // Update ATC button variant ID
      var atcBtn = card.querySelector('.product-card__atc');
      if (atcBtn) {
        atcBtn.dataset.variantId = select.value;
      }

      // Update price display
      var priceEl = document.getElementById('price-' + productId);
      if (priceEl && price) {
        var formatted = WhamTan.formatMoney(parseInt(price, 10));
        if (comparePrice && parseInt(comparePrice, 10) > parseInt(price, 10)) {
          priceEl.innerHTML =
            formatted +
            '<span class="was">' +
            WhamTan.formatMoney(parseInt(comparePrice, 10)) +
            '</span>';
        } else {
          priceEl.innerHTML = formatted;
        }
      }
    });
  });

  // ============================================================
  // 7. PRODUCT CARD — Subscription / selling plan toggle
  // ============================================================
  document.querySelectorAll('[name^="purchase_type_"]').forEach(function (radio) {
    radio.addEventListener('change', function () {
      var productId = radio.dataset.productCard;
      var card = document.querySelector('[data-product-id="' + productId + '"]');
      if (!card) return;

      var planSelect = card.querySelector('[data-product-card-plan-select]');
      if (planSelect) {
        planSelect.style.display = radio.value === 'subscription' ? 'block' : 'none';
      }

      // Toggle visual active state on labels
      var labels = card.querySelectorAll('.selling-plan__option');
      labels.forEach(function (lbl) { lbl.classList.remove('selling-plan__option--active'); });
      if (radio.parentElement) radio.parentElement.classList.add('selling-plan__option--active');
    });
  });

  // ============================================================
  // 8. CART COUNT HELPERS
  // ============================================================
  var WhamTan = window.WhamTan = window.WhamTan || {};

  WhamTan.updateCartCount = function (count) {
    var badge = document.getElementById('cart-count');
    if (!badge) return;
    badge.textContent = count;
    if (count > 0) {
      badge.classList.remove('nav__cart-count--hidden');
    } else {
      badge.classList.add('nav__cart-count--hidden');
    }
  };

  WhamTan.refreshCartCount = function () {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        WhamTan.updateCartCount(cart.item_count);
      })
      .catch(function () {});
  };

  // ============================================================
  // 9. MONEY FORMATTER (matches Shopify's default GBP format)
  // ============================================================
  WhamTan.formatMoney = function (cents) {
    var pounds = cents / 100;
    return '£' + pounds.toFixed(2).replace(/\.00$/, '');
  };

  // ============================================================
  // 10. NEWSLETTER FORM — visual feedback on submit
  //     (native Shopify contact form handles actual submission)
  // ============================================================
  var newsletterForm = document.querySelector('#newsletter-form');
  if (newsletterForm) {
    newsletterForm.addEventListener('submit', function () {
      var btn = newsletterForm.querySelector('button[type="submit"]');
      if (btn) {
        btn.textContent = '✓ You\'re in!';
        btn.style.background = '#2dbe6c';
        btn.style.color = '#fff';
      }
    });
  }

})();

// ── Purchase type toggle (Subscribe & Save) ──
// ── Fetch selling plan price from Shopify and update display ──
function fetchPlanPrice(productId, planId, callback) {
  var card = document.querySelector('[data-product-id="' + productId + '"]');
  var handle = card ? (card.dataset.productHandle || productId) : productId;
  fetch('/products/' + handle + '.js')
    .then(function(r) { return r.json(); })
    .then(function(product) {
      var price = null;
      product.variants.forEach(function(variant) {
        if (variant.selling_plan_allocations) {
          variant.selling_plan_allocations.forEach(function(alloc) {
            if (String(alloc.selling_plan_id) === String(planId)) {
              price = alloc.price;
            }
          });
        }
      });
      if (price !== null && callback) callback(price);
    })
    .catch(function() {});
}

function formatMoney(cents) {
  return '£' + (cents / 100).toFixed(2);
}

function setPurchaseType(btn, productId, type, planId) {
  var container = btn.closest('.product-card__selling-plans');
  container.querySelectorAll('.purchase-btn').forEach(function(b) {
    b.classList.remove('purchase-btn--active');
  });
  btn.classList.add('purchase-btn--active');

  var planInput = document.getElementById('selling-plan-' + productId);
  var freqEl = document.getElementById('plan-freq-' + productId);
  var priceEl = document.getElementById('price-' + productId);
  var card = btn.closest('.product-card');
  var basePrice = card ? parseInt(card.dataset.basePrice || 0) : 0;

  if (type === 'subscription') {
    // Use provided planId or fall back to first plan
    var effectivePlanId = planId;
    if (!effectivePlanId && planInput) {
      effectivePlanId = planInput.dataset.firstPlanId;
    }
    if (planInput && effectivePlanId) planInput.value = effectivePlanId;
    if (freqEl) freqEl.style.display = '';
    // Fetch and update price display
    fetchPlanPrice(productId, effectivePlanId || planId, function(price) {
      if (priceEl) {
        priceEl.innerHTML = '<span style="text-decoration:line-through;opacity:0.5;font-size:0.85em;">' + formatMoney(basePrice) + '</span> <strong>' + formatMoney(price) + '</strong>';
      }
      // Update the sub button price too
      var subPriceEl = btn.querySelector('.purchase-btn__price');
      if (subPriceEl) {
        subPriceEl.innerHTML = '<s style="opacity:0.5;font-size:0.8em;">' + formatMoney(basePrice) + '</s> ' + formatMoney(price);
      }
    });
  } else {
    if (planInput) planInput.value = '';
    if (freqEl) freqEl.style.display = 'none';
    // Reset to base price
    if (priceEl && basePrice) {
      priceEl.innerHTML = formatMoney(basePrice);
    }
  }
}

function setFrequency(btn, productId, planId) {
  var container = btn.closest('.plan-frequency');
  container.querySelectorAll('.freq-btn').forEach(function(b) {
    b.classList.remove('freq-btn--active');
  });
  btn.classList.add('freq-btn--active');
  var planInput = document.getElementById('selling-plan-' + productId);
  if (planInput) planInput.value = planId;

  // Update subscribe button price for this plan
  var card = btn.closest('.product-card');
  var basePrice = card ? parseInt(card.dataset.basePrice || 0) : 0;
  var subBtn = card ? card.querySelector('.purchase-btn[data-type="subscription"]') : null;

  fetchPlanPrice(productId, planId, function(price) {
    if (subBtn) {
      var subPriceEl = subBtn.querySelector('.purchase-btn__price');
      if (subPriceEl) {
        subPriceEl.innerHTML = '<s style="opacity:0.5;font-size:0.8em;">' + formatMoney(basePrice) + '</s> ' + formatMoney(price);
      }
      // Update badge if discount changed
    }
    // Update main price display if subscribe is active
    if (subBtn && subBtn.classList.contains('purchase-btn--active')) {
      var priceEl = document.getElementById('price-' + productId);
      if (priceEl) {
        priceEl.innerHTML = '<span style="text-decoration:line-through;opacity:0.5;font-size:0.85em;">' + formatMoney(basePrice) + '</span> <strong>' + formatMoney(price) + '</strong>';
      }
    }
  });
}
